import { Box, Tab, formatMinutes, getSpacing, pn, Skeleton, Stack } from "@compass/core";
import { Tabs } from "@mui/material";
import { Chart, Export, Label, Legend, Series, Tooltip, ValueAxis, VisualRange, Title, Font } from "devextreme-react/chart";
import { HomeServiceDashboardRow } from "generated/openApi";
import { useServiceData } from "../contexts";
import { DashboardCard } from "../components/DashboardCard";
import { useRef } from "react";
import { ServiceFilterControls } from "./ServiceFilterControls";
import { ServicePeriod } from "../types";
import { DASHBOARD_CHART_VIEW_CONFIG, DashboardChartView, getDashboardDistributionColor } from "../types";
import { DashboardChartViewSelect } from "../components/DashboardChartViewSelect";
import { useState } from "react";

const ServiceChart = (props: { 
    rows: HomeServiceDashboardRow[] | undefined;
    loading: boolean;
    chartRef: React.RefObject<Chart>;
    periodLabel: string;
    serviceColorMap: Record<string, string>;
    chartView: DashboardChartView;
}) => {
    
    return props.loading ? (
        <Stack direction="row" spacing="lg">
            <Skeleton variant="rounded" width={"70%"} height={400} />
            <Skeleton variant="rounded" width={"30%"} height={200} />
        </Stack>
    ) : (
        <Stack width="100%" height="100%" p={getSpacing("md")}>
            <Chart
                ref={props.chartRef}
                id="chart"
                dataSource={props.rows}
                height="100%"
                rotated={props.chartView === 'horizontalBar'}
                customizePoint={(args: any) => {
                    return { color: props.serviceColorMap[args.data.serviceType] ?? getDashboardDistributionColor(args.index) };
                }}
                onPointHoverChanged={(pointInfo: any) => {
                    if (pointInfo.target.isHovered()) {
                        pointInfo.target.showTooltip();
                    } else {
                        pointInfo.target.hideTooltip();
                    }
                }}
            >
                <Title 
                    text="Services Dashboard"
                    subtitle={`Period: ${props.periodLabel}`}
                >
                    <Font size={18} weight={600} />
                </Title>
                <Series
                    argumentField={pn<HomeServiceDashboardRow>("serviceType")}
                    valueField={pn<HomeServiceDashboardRow>("totalStudents")}
                    type="bar"
                />
                <Tooltip
                    enabled={false}
                    customizeTooltip={(arg) => {
                        return {
                            text: props.chartView === 'comparisonBar'
                                ? `${arg.argumentText}: ${arg.seriesName} — ${arg.valueText} students`
                                :
                                `${arg.point.data.serviceType}<br/>` +
                                `Students: ${arg.point.data.totalStudents}<br/>` +
                                `Total: ${formatMinutes(arg.point.data.totalDurationInMinutes)}`,
                        };
                    }}
                />
                <ValueAxis>
                    <VisualRange startValue={0} />
                    <Label customizeText={(arg) => `${arg.valueText}`} />
                </ValueAxis>
                <Legend visible={false} />
                <Export enabled={false} />
            </Chart>
        </Stack>
    );
};

const ServiceChartCardContent = (props: { 
    chartRef: React.RefObject<Chart>;
    periodLabel: string;
}) => {
    const { currentRows, loading, selectedPeriod, updateSelectedPeriod, serviceColorMap } = useServiceData();
    const [chartView, setChartView] = useState<DashboardChartView>(DASHBOARD_CHART_VIEW_CONFIG.services.recommendedView);
    const handleTabChange = (_event: React.SyntheticEvent, newValue: ServicePeriod) => {
        updateSelectedPeriod(newValue);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack 
                direction="row" 
                justifyContent="space-between" 
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                gap={2}
                sx={{ mb: 2 }}
            >
                <Tabs value={selectedPeriod} onChange={handleTabChange}>
                    <Tab label="This Month" value={ServicePeriod.ThisMonth} />
                    <Tab label="Previous Month" value={ServicePeriod.PreviousMonth} />
                </Tabs>
                <Box sx={{
                    flexGrow: 1,
                    minWidth: 250,
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                }}>
                    <ServiceFilterControls />
                </Box>
                <DashboardChartViewSelect dashboard="services" value={chartView} onChange={setChartView} />
            </Stack>

            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <ServiceChart 
                    rows={currentRows}
                    loading={loading} 
                    chartRef={props.chartRef}
                    periodLabel={props.periodLabel}
                    serviceColorMap={serviceColorMap}
                    chartView={chartView}
                />
            </Box>
        </Box>
    );
};

export const ServiceChartCard = () => {
    const { selectedPeriod } = useServiceData();
    
    // Reference to the Chart instance
    const chartRef = useRef<Chart>(null);

    const periodLabel = selectedPeriod === ServicePeriod.ThisMonth ? "This Month" : "Previous Month";

    // Export handler - passed directly as prop to DashboardCard
    const handleExport = () => {
        if (chartRef.current) {
            const filenamePeriod = selectedPeriod === ServicePeriod.ThisMonth ? "ThisMonth" : "PreviousMonth";
            chartRef.current.instance.exportTo(`Services_${filenamePeriod}`, 'png');
        }
    };

    return (
        <DashboardCard title="Services" onExport={handleExport}>
            <ServiceChartCardContent chartRef={chartRef} periodLabel={periodLabel} />
        </DashboardCard>
    );
};
